import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { IWorkflowStep } from '~/client/graph'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import OperationRule from '~/client/src/shared/models/OperationRule'
import User from '~/client/src/shared/models/User'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'

import BallInCourtRulesTableRow from './components/BallInCourtRulesTableRow/BallInCourtRulesTableRow'

// localization: translated

interface IProps {
  bicOperationRules: OperationRule[]
  // The rule the "+ Add" placeholder became, still keyed as the placeholder
  newRuleId?: string
  allWorkflowSteps?: IWorkflowStep[]

  getUsersFromAssignment: (assignmentExpressions: string[]) => User[]
  onRemoveRuleClick: (ruleId: string) => void
  onDuplicateRuleClick: (ruleId: string) => void
  onChangeUserForRule: (
    rule: OperationRule,
    userId: string,
    isNew?: boolean,
  ) => void
  onRuleExpressionChange: (
    rule: OperationRule,
    locationAndCompExpression: string,
  ) => void
  onFieldToggleForRule?: (
    rule: OperationRule,
    fieldId: string,
    isSelected: boolean,
  ) => void
  onFieldsToggleForRule?: (
    rule: OperationRule,
    fieldIds: string[],
    isSelected: boolean,
  ) => void
  onDiffCommitForRule?: (
    rule: OperationRule,
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ) => void
  onRenameRule: (rule: OperationRule, name: string) => void
  onCompanyDiffCommitForRule?: (
    rule: OperationRule,
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ) => void

  tagsStore?: TagsStore
  companiesStore?: CompaniesStore
}

@inject('tagsStore', 'companiesStore')
@observer
export default class BallInCourtRulesTable extends React.Component<IProps> {
  public render() {
    const {
      bicOperationRules,
      getUsersFromAssignment,
      onDuplicateRuleClick,
      onRemoveRuleClick,
      onChangeUserForRule,
      onRuleExpressionChange,
      onFieldToggleForRule,
      onFieldsToggleForRule,
      onDiffCommitForRule,
      onRenameRule,
      onCompanyDiffCommitForRule,
      allWorkflowSteps,
      tagsStore,
      companiesStore,
    } = this.props

    if (!tagsStore.isDataReceived) {
      return (
        <div className="tw:flex tw:justify-center tw:p-8">
          <Spinner />
        </div>
      )
    }

    if (!bicOperationRules?.length) {
      return (
        <p className="tw:px-5 tw:py-8 tw:text-center tw:text-app-body tw:text-muted-foreground">
          {Localization.translator.noRules}
        </p>
      )
    }

    return (
      <div className="tw:flex tw:flex-col tw:gap-3 tw:p-5">
        {bicOperationRules.map((bicRule, index) => (
          <BallInCourtRulesTableRow
            key={this.getRowKey(bicRule)}
            bicRule={bicRule}
            ruleIndex={index + 1}
            allWorkflowSteps={allWorkflowSteps}
            getUsersFromAssignment={getUsersFromAssignment}
            onDuplicateRuleClick={onDuplicateRuleClick}
            onRemoveRuleClick={onRemoveRuleClick}
            onChangeUserForRule={onChangeUserForRule}
            onRuleExpressionChange={onRuleExpressionChange}
            onFieldToggleForRule={onFieldToggleForRule}
            onFieldsToggleForRule={onFieldsToggleForRule}
            onDiffCommitForRule={onDiffCommitForRule}
            onRenameRule={onRenameRule}
            onCompanyDiffCommitForRule={onCompanyDiffCommitForRule}
            tagsStore={tagsStore}
            companiesStore={companiesStore}
          />
        ))}
      </div>
    )
  }

  // The placeholder's first edit gives it an id of its own. Its card keeps the placeholder's key,
  // or React would remount it between the mousedown and the click of the edit that did it: a
  // name committed on blur by a click on the card's Add User or Add Company.
  private getRowKey = (rule: OperationRule): string =>
    !rule.isExisting || rule.id === this.props.newRuleId
      ? OperationRule.NEW_ID
      : rule.id
}
